// Sub-lote 3.C — Schema visual declarativo para "Meu Negócio até agora".
//
// Fonte única para renderizar os blocos de business_workspace tanto no
// painel lateral do workspace, no StepFinalizar quanto na página
// /membros/meu-negocio. Evita depender de key.replace(/_/g," ") e mantém
// labels amigáveis, ordem e formato consistentes.
//
// Regras:
// - Blocos = colunas jsonb de business_workspace (whitelist).
// - Cada bloco declara os campos conhecidos + labels amigáveis.
// - Campos desconhecidos ainda aparecem via friendlyLabel() (fallback).
// - Chaves internas ("_mission_id", "_finalized_at", etc.) nunca são
//   exibidas.
// - Sem IA, sem inferência: dependências entre blocos são estáticas.

import {
  Briefcase, Target, Users, Package, Palette, Megaphone,
  ClipboardList, TrendingUp, Handshake, Mail, MoreHorizontal,
  type LucideIcon,
} from "lucide-react";

export type FieldFormat = "text" | "long" | "money" | "percent" | "number" | "bool" | "list";

export type FieldSpec = {
  key: string;
  label: string;
  format?: FieldFormat;
};

export type BlockKey =
  | "model" | "niche" | "audience" | "offer" | "products"
  | "pricing" | "brand" | "channels" | "sales_plan"
  | "prospects" | "proposals" | "extras";

export type BlockSpec = {
  key: BlockKey;
  title: string;
  hint: string;
  icon: LucideIcon;
  fields: FieldSpec[];
  /** Outros blocos cujos dados são úteis ao construir este. Ordem = prioridade. */
  dependsOn: BlockKey[];
};

const F = (key: string, label: string, format: FieldFormat = "text"): FieldSpec => ({ key, label, format });

export const BLOCKS: BlockSpec[] = [
  {
    key: "model",
    title: "Modelo de negócio",
    hint: "Como o negócio ganha dinheiro.",
    icon: Briefcase,
    fields: [
      F("revenue_model", "Como ganha dinheiro"),
      F("delivery_mode", "Forma de entrega"),
      F("ticket_average", "Ticket médio", "money"),
      F("main_cost", "Principal custo"),
      F("promise", "Promessa central", "long"),
      F("value_hypothesis", "Hipótese de valor", "long"),
    ],
    dependsOn: [],
  },
  {
    key: "niche",
    title: "Nicho",
    hint: "O recorte específico do mercado.",
    icon: Target,
    fields: [
      F("niche_name", "Nicho escolhido"),
      F("segment", "Segmento"),
      F("why", "Por que este nicho", "long"),
    ],
    dependsOn: ["model"],
  },
  {
    key: "audience",
    title: "Público",
    hint: "Para quem o negócio resolve um problema.",
    icon: Users,
    fields: [
      F("persona_name", "Persona"),
      F("pain", "Dor principal", "long"),
      F("context", "Contexto de compra", "long"),
      F("age_range", "Faixa etária"),
      F("region", "Onde está"),
    ],
    dependsOn: ["model", "niche"],
  },
  {
    key: "offer",
    title: "Proposta de valor",
    hint: "A promessa clara do negócio.",
    icon: Megaphone,
    fields: [
      F("headline", "Frase principal"),
      F("promise", "Promessa", "long"),
      F("differentiators", "Diferenciais", "list"),
      F("guarantee", "Garantia"),
    ],
    dependsOn: ["model", "niche", "audience"],
  },
  {
    key: "products",
    title: "Produtos e serviços",
    hint: "O que exatamente é entregue.",
    icon: Package,
    fields: [
      F("catalog", "Catálogo", "list"),
      F("core_product", "Produto/serviço principal"),
      F("upsell", "Upsell/adicional"),
      F("format", "Formato da entrega"),
    ],
    dependsOn: ["offer", "audience"],
  },
  {
    key: "pricing",
    title: "Preços e margens",
    hint: "Quanto cobra e quanto sobra.",
    icon: ClipboardList,
    fields: [
      F("price", "Preço praticado", "money"),
      F("cost", "Custo direto", "money"),
      F("margin_pct", "Margem", "percent"),
      F("payment_terms", "Condições de pagamento"),
      F("recurrence", "Recorrência"),
    ],
    dependsOn: ["model", "products"],
  },
  {
    key: "brand",
    title: "Marca",
    hint: "Nome, identidade e tom de voz.",
    icon: Palette,
    fields: [
      F("name", "Nome da marca"),
      F("tagline", "Tagline"),
      F("tone", "Tom de voz"),
      F("colors", "Paleta", "list"),
    ],
    dependsOn: ["audience", "offer"],
  },
  {
    key: "channels",
    title: "Canais",
    hint: "Onde as pessoas encontram o negócio.",
    icon: TrendingUp,
    fields: [
      F("primary", "Canal principal"),
      F("secondary", "Canais de apoio", "list"),
      F("budget", "Investimento mensal", "money"),
    ],
    dependsOn: ["audience", "model"],
  },
  {
    key: "sales_plan",
    title: "Plano de vendas",
    hint: "Como chegar aos primeiros clientes.",
    icon: Target,
    fields: [
      F("first_clients", "Primeiros clientes", "long"),
      F("weekly_actions", "Ações semanais", "list"),
      F("goal_30d", "Meta em 30 dias"),
    ],
    dependsOn: ["channels", "offer"],
  },
  {
    key: "prospects",
    title: "Lista de prospecção",
    hint: "Contatos reais para o primeiro ciclo.",
    icon: Handshake,
    fields: [
      F("list_size", "Tamanho da lista", "number"),
      F("segments", "Segmentos", "list"),
      F("script", "Script inicial", "long"),
    ],
    dependsOn: ["audience", "sales_plan"],
  },
  {
    key: "proposals",
    title: "Propostas",
    hint: "Modelo de proposta ou orçamento.",
    icon: Mail,
    fields: [
      F("template", "Modelo", "long"),
      F("terms", "Termos comerciais"),
      F("delivery_time", "Prazo de entrega"),
    ],
    dependsOn: ["offer", "pricing"],
  },
  {
    key: "extras",
    title: "Outros",
    hint: "Configurações adicionais do negócio.",
    icon: MoreHorizontal,
    fields: [],
    dependsOn: [],
  },
];

export const BLOCK_BY_KEY: Record<string, BlockSpec> = Object.fromEntries(
  BLOCKS.map((b) => [b.key, b]),
);

export function isInternalKey(k: string): boolean {
  return k.startsWith("_");
}

export function hasBlockContent(v: unknown): boolean {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  return Object.keys(v as Record<string, unknown>).some(
    (k) => !isInternalKey(k) && !isEmptyValue((v as any)[k]),
  );
}

export function isEmptyValue(v: unknown): boolean {
  if (v == null || v === "") return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v as any).length === 0;
  return false;
}

export function friendlyLabel(key: string): string {
  const map: Record<string, string> = {
    notes: "Anotações",
    summary: "Resumo",
    goal: "Objetivo",
  };
  if (map[key]) return map[key];
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format a raw value into a display string, given a FieldFormat hint.
 * Robust to strings storing numbers ("R$ 320"), option objects ({label, value}),
 * arrays of strings/objects, booleans, and null.
 */
export function formatValue(v: unknown, format: FieldFormat = "text"): string {
  if (v == null || v === "") return "";
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  if (Array.isArray(v)) {
    return v
      .map((item) => (typeof item === "object" && item ? String((item as any).label ?? (item as any).value ?? "") : String(item)))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof v === "object") {
    const o = v as any;
    if (o.label != null || o.value != null) {
      const base = String(o.label ?? o.value);
      return o.other ? `${base}: ${o.other}` : base;
    }
    return JSON.stringify(v);
  }
  const s = String(v);
  if (format === "money") {
    const n = Number(String(s).replace(/[^\d.,-]/g, "").replace(",", "."));
    if (Number.isFinite(n) && s.match(/^[\d.,\s-]+$/)) {
      return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    }
    return s;
  }
  if (format === "percent") {
    const n = Number(s);
    if (Number.isFinite(n)) return `${n}%`;
    return s;
  }
  return s;
}

/**
 * Build renderable field rows for a block, preserving spec order, then
 * appending unknown-but-non-empty keys as fallbacks. Silently drops empty
 * values and internal keys.
 */
export function blockFieldRows(
  block: BlockSpec,
  raw: unknown,
): Array<{ key: string; label: string; value: string; format: FieldFormat; long: boolean }> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  const src = raw as Record<string, unknown>;
  const seen = new Set<string>();
  const rows: Array<{ key: string; label: string; value: string; format: FieldFormat; long: boolean }> = [];
  for (const f of block.fields) {
    seen.add(f.key);
    if (!(f.key in src)) continue;
    if (isEmptyValue(src[f.key])) continue;
    rows.push({
      key: f.key,
      label: f.label,
      value: formatValue(src[f.key], f.format),
      format: f.format ?? "text",
      long: f.format === "long",
    });
  }
  for (const k of Object.keys(src)) {
    if (seen.has(k) || isInternalKey(k)) continue;
    if (isEmptyValue(src[k])) continue;
    rows.push({
      key: k,
      label: friendlyLabel(k),
      value: formatValue(src[k]),
      format: "text",
      long: typeof src[k] === "string" && (src[k] as string).length > 80,
    });
  }
  return rows;
}

/**
 * Merge a persisted block value with a pending draft (deliverable state
 * for the current mission), for the "live preview" of the block the user
 * is actively editing. Draft wins where it has a non-empty value.
 */
export function mergeBlockWithDraft(persisted: unknown, draft: unknown): Record<string, unknown> {
  const base: Record<string, unknown> =
    persisted && typeof persisted === "object" && !Array.isArray(persisted)
      ? { ...(persisted as Record<string, unknown>) }
      : {};
  if (draft && typeof draft === "object" && !Array.isArray(draft)) {
    for (const [k, v] of Object.entries(draft as Record<string, unknown>)) {
      if (isInternalKey(k)) continue;
      if (isEmptyValue(v)) continue;
      base[k] = v;
    }
  }
  return base;
}

/**
 * Pick up to `max` prior decisions (block/field/value tuples) from the
 * user's persisted workspace, prioritising blocks the current block
 * declares as `dependsOn`. Purely deterministic — no inference.
 */
export function pickPriorDecisions(
  currentBlockKey: BlockKey | null,
  workspace: Record<string, unknown> | null | undefined,
  max = 3,
): Array<{ block: BlockSpec; label: string; value: string }> {
  if (!workspace || !currentBlockKey) return [];
  const current = BLOCK_BY_KEY[currentBlockKey];
  if (!current) return [];
  const out: Array<{ block: BlockSpec; label: string; value: string }> = [];
  for (const depKey of current.dependsOn) {
    const dep = BLOCK_BY_KEY[depKey];
    if (!dep) continue;
    const raw = workspace[depKey];
    const rows = blockFieldRows(dep, raw).filter((r) => !r.long);
    for (const r of rows) {
      out.push({ block: dep, label: r.label, value: r.value });
      if (out.length >= max) return out;
    }
  }
  return out.slice(0, max);
}
